import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { User, Music, BarChart3, Calendar, Plus, Save, Upload, CreditCard, AlertTriangle, ArrowRight, Crown, Settings, Shield, ExternalLink, Video, Link2, CheckCircle, Users, Camera, Wallet, Coins, Sparkles, Trash2, TrendingUp, Share2, Pencil } from 'lucide-react';
import { RoleApplicationSection } from '../components/RoleApplicationSection';
import { ProfilePhotoUpload } from '../components/ProfilePhotoUpload';
import { PhotoGallery } from '../components/PhotoGallery';
import { ProfileCompletionCard } from '../components/ProfileCompletionCard';
import { BannerUpload } from '../components/BannerUpload';
import { StripeConnectCard } from '../components/StripeConnectCard';
import { CoinPurchaseModal } from '../components/CoinPurchaseModal';
import { ListenerTierCard } from '../components/ListenerTier';
import TrackUploadForm from '../components/TrackUploadForm';
import VideoUploadForm from '../components/VideoUploadForm';
import VideoManager from '../components/VideoManager';
import { PostComposer } from '../components/PostComposer';
import { ChartRequestModal } from '../components/ChartRequestModal';
import { CreatorChartAnalytics } from '../components/CreatorChartAnalytics';
import { CreatorAnalyticsDashboard } from '../components/CreatorAnalyticsDashboard';
import { ContentBoostManager } from '../components/ContentBoostManager';
import { EditTrackModal } from '../components/EditTrackModal';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';
import { api } from '../utils/api';



export default function DashboardPage() {
  const { user, isAuthenticated, isArtist, getAuthHeaders, requiresSubscription, hasActiveSubscription, artistTier, isOwner, bypassBilling, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [genres, setGenres] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [artistProfile, setArtistProfile] = useState({
    stage_name: '',
    bio: '',
    neighborhood: '',
    street: '',  // Custom street entry
    city: '',
    region: '',  // MD, DC, VA
    genres: [],
    socials: { instagram: '', youtube: '', twitter: '', tiktok: '', soundcloud: '' },
    booking_links: {},
    avatar_url: null,
    banner_url: null
  });
  const [tracks, setTracks] = useState([]);
  const [videos, setVideos] = useState([]);
  const [newTrack, setNewTrack] = useState({ 
    title: '', 
    genre: '', 
    description: '',
    spotify_url: '',
    apple_music_url: '',
    isrc: '',
    release_date: '',
    label: '',
    ownership_confirmed: false
  });
  const [newVideo, setNewVideo] = useState({
    title: '',
    linked_track_id: '',
    youtube_url: '',
    vimeo_url: '',
    description: ''
  });
  const [coinWallet, setCoinWallet] = useState({ coin_balance: 0 });
  const [showCoinModal, setShowCoinModal] = useState(false);
  const [chartRequestTrack, setChartRequestTrack] = useState(null);
  const [showChartRequestModal, setShowChartRequestModal] = useState(false);
  const [editingTrack, setEditingTrack] = useState(null);
  const [showEditTrackModal, setShowEditTrackModal] = useState(false);
  const [earnings, setEarnings] = useState({
    earnings: { total_usd: 0 },
    streams: { total_streams: 0, peak_viewers: 0, total_viewers: 0, avg_viewers: 0 },
    followers: { total: 0, new_in_period: 0 }
  });
  
  const regions = ['Maryland', 'Washington DC', 'Virginia'];
  const defaultTab = searchParams.get('tab') || 'profile';

  useEffect(() => {
    // Wait for auth to finish loading before checking authentication
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated, authLoading]);

  const fetchData = async () => {
    try {
      const [hoodsResult, genresResult] = await Promise.all([
        api.get('/reference/neighborhoods'),
        api.get('/reference/genres')
      ]);
      
      if (hoodsResult.ok && hoodsResult.data) setNeighborhoods(hoodsResult.data.neighborhoods || []);
      if (genresResult.ok && genresResult.data) setGenres(genresResult.data.genres || []);

      // Fetch subscription details if user is an artist
      if (hasActiveSubscription || isOwner) {
        const subResult = await api.get('/subscriptions/my-subscription');
        if (subResult.ok && subResult.data) setSubscription(subResult.data);
      }

      // Fetch coin wallet for all authenticated users
      if (isAuthenticated) {
        try {
          const walletResult = await api.get('/coins/wallet');
          if (walletResult.ok && walletResult.data) setCoinWallet(walletResult.data);
          
          // Fetch analytics overview
          const analyticsResult = await api.get('/analytics/overview', { time_range: 'month' });
          if (analyticsResult.ok && analyticsResult.data) setEarnings(analyticsResult.data);
        } catch (e) {
          console.log('Wallet/analytics fetch optional error:', e);
        }
      }

      if (isArtist && user?.user_id && (hasActiveSubscription || isOwner)) {
        // Fetch artist profile
        const artistsResult = await api.get('/artists');
        if (artistsResult.ok && artistsResult.data) {
          const artists = artistsResult.data;
          const myProfile = artists.find(a => a.user_id === user.user_id);
          if (myProfile) {
            setArtistProfile({
              stage_name: myProfile.stage_name || '',
              bio: myProfile.bio || '',
              neighborhood: myProfile.neighborhood || '',
              genres: myProfile.genres || [],
              socials: myProfile.socials || { instagram: '', youtube: '', twitter: '', tiktok: '', soundcloud: '' },
              booking_links: myProfile.booking_links || {},
              avatar_url: myProfile.avatar_url || null,
              banner_url: myProfile.banner_url || null
            });

            const tracksResult = await api.get('/tracks', { user_id: user.user_id });
            if (tracksResult.ok && tracksResult.data) setTracks(tracksResult.data);

            const videosResult = await api.get('/videos', { user_id: user.user_id });
            if (videosResult.ok && videosResult.data) setVideos(videosResult.data);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    // Validate required social links
    if (!artistProfile.socials.instagram) {
      toast.error('Instagram link is required');
      return;
    }
    if (!artistProfile.socials.youtube) {
      toast.error('YouTube link is required');
      return;
    }
    
    const additionalPlatforms = ['twitter', 'tiktok', 'soundcloud', 'spotify', 'facebook'];
    const hasAdditional = additionalPlatforms.some(p => artistProfile.socials[p]);
    if (!hasAdditional) {
      toast.error('At least one additional social platform is required (Twitter, TikTok, etc.)');
      return;
    }

    setSaving(true);
    try {
      const result = await api.post('/artists/profile', artistProfile);

      if (result.ok) {
        toast.success('Profile saved successfully!');
      } else {
        toast.error(result.error || 'Failed to save profile');
      }
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTrack = async () => {
    // Validate required fields
    if (!newTrack.title) {
      toast.error('Track title is required');
      return;
    }
    if (!newTrack.genre) {
      toast.error('Primary genre is required for chart eligibility');
      return;
    }
    if (!newTrack.spotify_url) {
      toast.error('Spotify track link is required. All music must be released on DSPs.');
      return;
    }
    if (!newTrack.ownership_confirmed) {
      toast.error('You must confirm ownership/rights to submit this track.');
      return;
    }

    try {
      const result = await api.post('/tracks', {
          title: newTrack.title,
          primary_genre: newTrack.genre,
          spotify_url: newTrack.spotify_url,
          apple_music_url: newTrack.apple_music_url || null,
          isrc: newTrack.isrc || null,
          release_date: newTrack.release_date || null,
          label: newTrack.label || null,
          description: newTrack.description || null,
          ownership_confirmed: newTrack.ownership_confirmed
      });

      if (result.ok && result.data) {
        toast.success(`DSP track added! Eligible for: ${result.data.chart_eligible_for?.join(', ') || 'charts'}`);
        setNewTrack({ 
          title: '', 
          genre: '', 
          description: '',
          spotify_url: '',
          apple_music_url: '',
          isrc: '',
          release_date: '',
          label: '',
          ownership_confirmed: false
        });
        fetchData();
      } else {
        toast.error(result.error || 'Failed to add track');
      }
    } catch (error) {
      toast.error('Failed to add track');
    }
  };

  const handleDeleteTrack = async (trackId, trackTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${trackTitle}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      const result = await api.delete(`/tracks/${trackId}`);
      
      if (result.ok) {
        toast.success('Track deleted successfully');
        fetchData();
      } else {
        toast.error(result.error || 'Failed to delete track');
      }
    } catch (error) {
      toast.error('Failed to delete track');
    }
  };

  const handleCreateVideo = async () => {
    if (!newVideo.title) {
      toast.error('Video title is required');
      return;
    }
    if (!newVideo.linked_track_id) {
      toast.error('Please select the DSP song this video belongs to');
      return;
    }
    if (!newVideo.youtube_url && !newVideo.vimeo_url) {
      toast.error('Please provide a YouTube or Vimeo link');
      return;
    }

    try {
      const result = await api.post('/videos', {
          title: newVideo.title,
          linked_track_id: newVideo.linked_track_id,
          youtube_url: newVideo.youtube_url || null,
          vimeo_url: newVideo.vimeo_url || null,
          description: newVideo.description || null
      });

      if (result.ok) {
        toast.success('Official music video added!');
        setNewVideo({
          title: '',
          linked_track_id: '',
          youtube_url: '',
          vimeo_url: '',
          description: ''
        });
        fetchData();
      } else {
        toast.error(result.error || 'Failed to add video');
      }
    } catch (error) {
      toast.error('Failed to add video');
    }
  };

  const handleManageSubscription = async () => {
    try {
      const result = await api.post('/subscriptions/portal', { origin_url: window.location.origin });
      
      if (result.ok && result.data) {
        if (result.data.fallback) {
          toast.info(result.data.message);
        }
        window.location.href = result.data.url;
      } else {
        toast.error('Failed to access subscription portal');
      }
    } catch (error) {
      toast.error('Failed to manage subscription');
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Artist Paywall - Redirect to pricing if they don't have an active subscription
  if (requiresSubscription && !hasActiveSubscription && !isOwner) {
    return (
      <div className="min-h-screen py-12 px-4" data-testid="subscription-required">
        <div className="max-w-2xl mx-auto">
          <Card className="glass-card p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-secondary" />
            </div>
            <h1 className="font-heading text-3xl font-bold uppercase mb-4">
              Subscription Required
            </h1>
            <p className="text-muted-foreground mb-6">
              Artist accounts require an active subscription to access the dashboard and upload content.
              Choose a plan that fits your needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                className="bg-secondary hover:bg-secondary/90"
                onClick={() => navigate('/pricing')}
                data-testid="go-to-pricing-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                View Subscription Plans
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      {/* Dashboard Header Bar */}
      <div className="bg-card border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <Settings className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold">Artist Dashboard</h1>
                <p className="text-sm text-muted-foreground">Manage your music & profile</p>
              </div>
            </div>
            
            {/* Status Badges */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* NOTE: Platform Owner badge removed from public display per platform policy */}
              {/* Owners are shown as Artists publicly. Admin access is backend-only. */}
              {hasActiveSubscription && subscription && (
                <>
                  <Badge className="bg-secondary/20 text-secondary">
                    <Crown className="w-3 h-3 mr-1" />
                    {subscription.tier_name}
                  </Badge>
                  <Button variant="outline" size="sm" onClick={handleManageSubscription} data-testid="manage-subscription-btn">
                    <Settings className="w-4 h-4 mr-2" />
                    Manage Plan
                  </Button>
                </>
              )}
              {artistProfile && (
                <Badge variant="outline" className={artistProfile.profile_complete ? "text-green-500 border-green-500/30" : "text-amber-500 border-amber-500/30"}>
                  {artistProfile.profile_complete ? "Profile Complete" : "Setup Incomplete"}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Quick Stats for Active Artists */}
        {hasActiveSubscription && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4 bg-card border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Music className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{tracks.length}</p>
                  <p className="text-xs text-muted-foreground">Tracks</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-card border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Video className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{videos.length}</p>
                  <p className="text-xs text-muted-foreground">Videos</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-card border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{tracks.reduce((a, t) => a + (t.plays || 0), 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Total Plays</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-card border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{tracks.reduce((a, t) => a + (t.requests_count || 0), 0)}</p>
                  <p className="text-xs text-muted-foreground">Requests</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Tabs Navigation */}
        <Tabs defaultValue={defaultTab}>
          <TabsList className="bg-card border border-border mb-6 flex-wrap p-1">
            <TabsTrigger value="profile" className="data-[state=active]:bg-primary/20">
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            {(hasActiveSubscription || isOwner) && (
              <>
                <TabsTrigger value="music" className="data-[state=active]:bg-primary/20">
                  <Music className="w-4 h-4 mr-2" />
                  Music
                </TabsTrigger>
                <TabsTrigger value="videos" className="data-[state=active]:bg-primary/20" data-testid="dashboard-videos-tab">
                  <Video className="w-4 h-4 mr-2" />
                  Videos
                </TabsTrigger>
                <TabsTrigger value="posts" className="data-[state=active]:bg-primary/20">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Posts
                </TabsTrigger>
                <TabsTrigger value="earnings" className="data-[state=active]:bg-primary/20">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Earnings
                </TabsTrigger>
                <TabsTrigger value="analytics" className="data-[state=active]:bg-primary/20">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Analytics
                </TabsTrigger>
                <TabsTrigger value="social" className="data-[state=active]:bg-primary/20">
                  <Share2 className="w-4 h-4 mr-2" />
                  Social
                </TabsTrigger>
              </>
            )}
            <TabsTrigger value="events" className="data-[state=active]:bg-primary/20">
              <Calendar className="w-4 h-4 mr-2" />
              Events
            </TabsTrigger>
            <TabsTrigger value="roles" className="data-[state=active]:bg-primary/20">
              <Users className="w-4 h-4 mr-2" />
              Roles
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            {/* Profile Completion Progress */}
            {hasActiveSubscription && (
              <ProfileCompletionCard 
                artistProfile={artistProfile}
                onScrollToField={(fieldId) => {
                  // Map field IDs to their testids
                  const testIdMap = {
                    'stage_name': 'stage-name-input',
                    'bio': 'bio-textarea',
                    'neighborhood': 'neighborhood-select',
                    'instagram': 'instagram-input',
                    'youtube': 'youtube-input',
                    'additional_social': 'twitter-input' // Focus on first additional option
                  };
                  const testId = testIdMap[fieldId] || fieldId;
                  const element = document.querySelector(`[data-testid="${testId}"]`);
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    setTimeout(() => element.focus(), 300);
                  }
                }}
              />
            )}

            {/* Profile Photo Gallery (compact) */}
            <PhotoGallery
              compact
              currentAvatarUrl={artistProfile.avatar_url}
              getAuthHeaders={getAuthHeaders}
              onAvatarChange={(newUrl) => {
                setArtistProfile(prev => ({ ...prev, avatar_url: newUrl }));
                fetchData();
              }}
            />

            {/* Banner Upload Section */}
            <div className="mt-6">
              <BannerUpload
                currentBannerUrl={artistProfile.banner_url}
                artistName={artistProfile.stage_name}
                getAuthHeaders={getAuthHeaders}
                onUploadSuccess={(newUrl) => {
                  setArtistProfile(prev => ({ ...prev, banner_url: newUrl }));
                  fetchData(); // Refresh data
                }}
              />
            </div>

            <Card className="glass-card border-border p-6 mt-6">
              <h2 className="font-heading text-2xl font-bold uppercase mb-6">
                Artist Profile
              </h2>

              <div className="grid gap-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Stage Name *</Label>
                    <Input
                      value={artistProfile.stage_name}
                      onChange={(e) => setArtistProfile({ ...artistProfile, stage_name: e.target.value })}
                      placeholder="Your artist name"
                      data-testid="stage-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Region *</Label>
                    <Select 
                      value={artistProfile.region} 
                      onValueChange={(v) => setArtistProfile({ ...artistProfile, region: v })}
                    >
                      <SelectTrigger data-testid="region-select">
                        <SelectValue placeholder="Select region (MD/DC/VA)" />
                      </SelectTrigger>
                      <SelectContent>
                        {regions.map((region) => (
                          <SelectItem key={region} value={region}>{region}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Location Details */}
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>City / Area *</Label>
                    <Select 
                      value={artistProfile.neighborhood} 
                      onValueChange={(v) => setArtistProfile({ ...artistProfile, neighborhood: v })}
                    >
                      <SelectTrigger data-testid="neighborhood-select">
                        <SelectValue placeholder="Select city/area" />
                      </SelectTrigger>
                      <SelectContent>
                        {neighborhoods.map((hood) => (
                          <SelectItem key={hood} value={hood}>{hood}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Street (Optional)</Label>
                    <Input
                      value={artistProfile.street || ''}
                      onChange={(e) => setArtistProfile({ ...artistProfile, street: e.target.value })}
                      placeholder="e.g., Arena Drive, U Street, Pennsylvania Ave"
                      data-testid="street-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Add your street name for Discover by Hood. No exact addresses needed.
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Bio *</Label>
                  <Textarea
                    value={artistProfile.bio}
                    onChange={(e) => setArtistProfile({ ...artistProfile, bio: e.target.value })}
                    placeholder="Tell your story..."
                    rows={4}
                    data-testid="bio-textarea"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Genres (select multiple)</Label>
                  <div className="flex flex-wrap gap-2">
                    {genres.map((genre) => (
                      <Badge
                        key={genre}
                        variant={artistProfile.genres.includes(genre) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => {
                          const newGenres = artistProfile.genres.includes(genre)
                            ? artistProfile.genres.filter(g => g !== genre)
                            : [...artistProfile.genres, genre];
                          setArtistProfile({ ...artistProfile, genres: newGenres });
                        }}
                      >
                        {genre}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Required Social Links */}
                <div className="space-y-4 pt-4 border-t border-border">
                  <div>
                    <h3 className="font-heading text-lg font-bold uppercase mb-2">Required Social Links</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Instagram + YouTube + at least one additional platform required
                    </p>
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1">
                        Instagram URL <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={artistProfile.socials.instagram || ''}
                        onChange={(e) => setArtistProfile({ 
                          ...artistProfile, 
                          socials: { ...artistProfile.socials, instagram: e.target.value }
                        })}
                        placeholder="https://instagram.com/yourusername"
                        data-testid="instagram-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1">
                        YouTube URL <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={artistProfile.socials.youtube || ''}
                        onChange={(e) => setArtistProfile({ 
                          ...artistProfile, 
                          socials: { ...artistProfile.socials, youtube: e.target.value }
                        })}
                        placeholder="https://youtube.com/@yourchannel"
                        data-testid="youtube-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Social Links */}
                <div className="space-y-4">
                  <h3 className="font-heading text-sm font-bold uppercase text-muted-foreground">
                    Additional Platforms (at least one required)
                  </h3>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Twitter/X</Label>
                      <Input
                        value={artistProfile.socials.twitter || ''}
                        onChange={(e) => setArtistProfile({ 
                          ...artistProfile, 
                          socials: { ...artistProfile.socials, twitter: e.target.value }
                        })}
                        placeholder="https://twitter.com/..."
                        data-testid="twitter-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>TikTok</Label>
                      <Input
                        value={artistProfile.socials.tiktok || ''}
                        onChange={(e) => setArtistProfile({ 
                          ...artistProfile, 
                          socials: { ...artistProfile.socials, tiktok: e.target.value }
                        })}
                        placeholder="https://tiktok.com/@..."
                        data-testid="tiktok-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>SoundCloud</Label>
                      <Input
                        value={artistProfile.socials.soundcloud || ''}
                        onChange={(e) => setArtistProfile({ 
                          ...artistProfile, 
                          socials: { ...artistProfile.socials, soundcloud: e.target.value }
                        })}
                        placeholder="https://soundcloud.com/..."
                        data-testid="soundcloud-input"
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={handleSaveProfile} 
                  disabled={saving}
                  className="w-fit"
                  data-testid="save-profile-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Profile'}
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* Music Tab */}
          {(hasActiveSubscription || isOwner) && (
            <TabsContent value="music">
              <div className="space-y-6">
                {/* Add Track - New Upload Form */}
                <Card className="glass-card border-border p-6">
                  <h2 className="font-heading text-xl font-bold uppercase mb-4 flex items-center gap-2">
                    <Music className="w-5 h-5 text-green-500" />
                    Add Track
                  </h2>
                  
                  {/* Platform Info */}
                  <div className="bg-muted/30 border border-border rounded-lg p-4 mb-6">
                    <p className="text-xs text-muted-foreground">
                      <strong className="text-foreground">Two ways to add music:</strong> Link from DSP (Spotify, Apple Music, etc.) for free,
                      or upload audio files directly if you&apos;re on Pro tier or above.
                    </p>
                  </div>

                  <TrackUploadForm
                    onTrackCreated={() => fetchData()}
                    getAuthHeaders={getAuthHeaders}
                    genres={genres}
                    artistProfile={artistProfile}
                  />
                  
                  {subscription && subscription.track_limit !== -1 && (
                    <p className="text-xs text-muted-foreground mt-3">
                      {tracks.length}/{subscription.track_limit} tracks used • {subscription.track_limit - tracks.length} remaining
                    </p>
                  )}
                </Card>

                {/* Add Music Video */}
                {/* Track List */}
                <Card className="glass-card border-border p-6">
                  <h2 className="font-heading text-xl font-bold uppercase mb-4">My Tracks ({tracks.length})</h2>
                  {tracks.length > 0 ? (
                    <div className="space-y-3">
                      {tracks.map((track) => (
                        <div key={track.track_id} className="flex items-center gap-4 p-3 rounded-lg bg-muted/30">
                          <div className="w-12 h-12 bg-muted rounded flex items-center justify-center">
                            {track.cover_url ? (
                              <img src={track.cover_url} alt={track.title} className="w-full h-full object-cover rounded" />
                            ) : (
                              <Music className="w-5 h-5 text-muted-foreground" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{track.title}</p>
                              {track.is_dsp_verified && (
                                <Badge className="text-xs bg-green-500/20 text-green-400 border-green-500/30">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  DSP Verified
                                </Badge>
                              )}
                            </div>
                            <div className="flex gap-3 text-sm text-muted-foreground mt-1">
                              {track.primary_genre && (
                                <Badge variant="outline" className="text-xs">
                                  {track.primary_genre_name || track.primary_genre}
                                </Badge>
                              )}
                              <span>{track.plays || 0} plays</span>
                              <span>{track.requests_count || 0} requests</span>
                            </div>
                            {track.spotify_url && (
                              <a 
                                href={track.spotify_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1 mt-1"
                              >
                                <ExternalLink className="w-3 h-3" />
                                Listen on Spotify
                              </a>
                            )}
                            {track.chart_eligible_for && track.chart_eligible_for.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {track.chart_eligible_for.map((chart, i) => (
                                  <span key={i} className="text-[10px] text-secondary">{chart}</span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingTrack(track);
                                setShowEditTrackModal(true);
                              }}
                              data-testid={`edit-track-${track.track_id}`}
                              title="Edit track"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-secondary hover:text-secondary/80 hover:bg-secondary/20"
                              onClick={(e) => {
                                e.stopPropagation();
                                setChartRequestTrack(track);
                                setShowChartRequestModal(true);
                              }}
                              data-testid={`request-top20-${track.track_id}`}
                              title="Request Top 20 Placement"
                            >
                              <TrendingUp className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTrack(track.track_id, track.title);
                              }}
                              data-testid={`delete-track-${track.track_id}`}
                              title="Delete track"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-8">No tracks yet. Create your first track above!</p>
                  )}
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Analytics Tab */}
          {hasActiveSubscription && (
            <TabsContent value="analytics">
              <div className="space-y-6">
                {/* Creator Analytics Dashboard */}
                <CreatorAnalyticsDashboard />

                {/* Content Boosts */}
                <ContentBoostManager />

                {/* Creator Chart Analytics (existing) */}
                <CreatorChartAnalytics artistHandle={artistProfile?.handle || user?.artist_id} />
                
                {/* View Full Analytics Button */}
                <Card className="glass-card border-border p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-heading font-bold text-lg">Detailed Analytics</h3>
                      <p className="text-sm text-muted-foreground">
                        View stream history, top supporters, earnings breakdown, and more.
                      </p>
                    </div>
                    <Button onClick={() => navigate('/analytics')} data-testid="full-analytics-btn">
                      <BarChart3 className="w-4 h-4 mr-2" />
                      View Full Analytics
                    </Button>
                  </div>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Posts Tab */}
          {(hasActiveSubscription || isOwner) && (
            <TabsContent value="posts">
              <PostComposer onPostCreated={fetchData} />
            </TabsContent>
          )}

          {/* Videos Tab */}
          <TabsContent value="videos">
            <div className="space-y-6">
              <VideoUploadForm
                onVideoCreated={() => fetchData()}
                getAuthHeaders={getAuthHeaders}
                tracks={tracks}
                artistProfile={artistProfile}
                tierConfig={{
                  direct_upload_enabled: user?.role === 'owner' || subscription?.tier === 'enterprise' || subscription?.tier === 'pro' || !!subscription?.active,
                  tier: user?.role === 'owner' ? 'enterprise' : (subscription?.tier || 'free'),
                }}
                uploadLimits={{
                  direct_uploads_remaining: user?.role === 'owner' ? 999 : (subscription?.video_limit || 5),
                }}
              />
              <VideoManager
                videos={videos}
                onRefresh={fetchData}
                getAuthHeaders={getAuthHeaders}
                tracks={tracks}
                artistProfile={artistProfile}
                user={user}
                subscription={subscription}
              />
            </div>
          </TabsContent>

          {/* Earnings Tab */}
          {hasActiveSubscription && (
            <TabsContent value="earnings">
              <div className="space-y-6">
                {/* Listener Tier Card */}
                <ListenerTierCard getAuthHeaders={getAuthHeaders} />

                {/* Coin Wallet Section */}
                <Card className="glass-card border-amber-500/30 p-6" data-testid="coin-wallet-card">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                        <Coins className="w-6 h-6 text-amber-400" />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-lg mb-1">Your Coin Wallet</h3>
                        <p className="text-sm text-muted-foreground">
                          Use coins to send gifts during live streams
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <Coins className="w-6 h-6 text-amber-400" />
                        <span className="text-3xl font-bold text-amber-400">{coinWallet.coin_balance?.toLocaleString() || 0}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">coins available</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-3">
                    <Button 
                      onClick={() => setShowCoinModal(true)}
                      className="bg-amber-500 hover:bg-amber-600 text-black"
                      data-testid="buy-coins-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Buy Coins
                    </Button>
                    <Button variant="outline" onClick={() => navigate('/analytics')} data-testid="view-analytics-btn">
                      <BarChart3 className="w-4 h-4 mr-2" />
                      View Analytics
                    </Button>
                  </div>
                </Card>

                {/* Stripe Connect Payout Setup */}
                <StripeConnectCard getAuthHeaders={getAuthHeaders} />

                {/* Fee Transparency Banner */}
                <Card className="glass-card border-secondary/30 p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-secondary/20 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-lg mb-2">How Earnings Work</h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        DMV Soundstage does not take money from streams. Charts are powered by real listener requests — not payments.
                      </p>
                      <div className="flex flex-wrap gap-2 text-xs">
                        <Badge variant="outline" className="bg-green-500/10 border-green-500/30 text-green-400">
                          Streams = Free
                        </Badge>
                        <Badge variant="outline" className="bg-green-500/10 border-green-500/30 text-green-400">
                          No hidden fees
                        </Badge>
                        <Badge variant="outline" className="bg-green-500/10 border-green-500/30 text-green-400">
                          Transparent payouts
                        </Badge>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Your Tier Fee Rate */}
                <Card className="glass-card border-border p-6">
                  <h3 className="font-heading font-bold text-lg mb-4">Your Platform Fee Rate</h3>
                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                    <div>
                      <p className="font-bold">{subscription?.tier_name || 'Loading...'}</p>
                      <p className="text-sm text-muted-foreground">Your current tier</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-primary">{subscription?.platform_fee_percent || '--'}%</p>
                      <p className="text-xs text-muted-foreground">on revenue-generating features</p>
                    </div>
                  </div>
                  <p className="mt-4 text-xs text-muted-foreground">
                    This fee applies to: Fan subscriptions, event ticket sales, and battle earnings. Higher tiers get lower fees.
                  </p>
                </Card>

                {/* Fee Breakdown */}
                <Card className="glass-card border-border p-6">
                  <h3 className="font-heading font-bold text-lg mb-4">Platform Fee Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                      <div>
                        <p className="font-medium">Fan Subscriptions</p>
                        <p className="text-xs text-muted-foreground">When fans subscribe to your channel</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">15% fee</p>
                        <p className="text-xs text-secondary">You keep 85%</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                      <div>
                        <p className="font-medium">Event Tickets</p>
                        <p className="text-xs text-muted-foreground">Paid tickets and RSVPs</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">12% fee</p>
                        <p className="text-xs text-secondary">You keep 88%</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                      <div>
                        <p className="font-medium">Battle Earnings</p>
                        <p className="text-xs text-muted-foreground">Prize pool winnings</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">10-15% fee</p>
                        <p className="text-xs text-secondary">Based on competition</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                      <div>
                        <p className="font-medium">Streams & Plays</p>
                        <p className="text-xs text-muted-foreground">Music playback</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-amber-400">0% fee</p>
                        <p className="text-xs text-amber-400">Always free</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-border">
                    <a href="/fees" className="text-sm text-secondary hover:underline flex items-center gap-1">
                      Learn more about our fee structure
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </Card>

                {/* Earnings Summary - Placeholder */}
                <Card className="glass-card border-border p-6 text-center">
                  <CreditCard className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="font-heading font-bold text-lg mb-2">Earnings Dashboard Coming Soon</h3>
                  <p className="text-muted-foreground text-sm">View your gross earnings, platform fees, and net payouts all in one place.</p>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Events Tab */}
          <TabsContent value="events">
            <Card className="glass-card border-border p-6 text-center">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="font-heading text-xl font-bold mb-2">Event Management Coming Soon</h2>
              <p className="text-muted-foreground">Create and manage your events from here.</p>
            </Card>
          </TabsContent>

          {/* Roles Tab */}
          <TabsContent value="roles">
            <RoleApplicationSection 
              user={user} 
              getAuthHeaders={getAuthHeaders}
              onRoleAdded={() => {
                fetchData();
                toast.success('Role profile created! Refreshing your data...');
              }}
            />
          </TabsContent>

          {/* Social Sync Tab */}
          {hasActiveSubscription && (
            <TabsContent value="social">
              <Card className="p-6 bg-gradient-to-br from-primary/10 to-purple-500/10 border-primary/20">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Share2 className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold">Social Media Sync</h2>
                    <p className="text-muted-foreground">Connect your social accounts and auto-post to expand your reach</p>
                  </div>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-background/50">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-sm">Connect Instagram, X, TikTok, YouTube</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-background/50">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-sm">Auto-post new content to connected platforms</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-background/50">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-sm">Import external posts to your DMV feed</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-background/50">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-sm">Schedule posts across all platforms</span>
                  </div>
                </div>
                
                <Button asChild className="w-full sm:w-auto">
                  <a href="/social-sync" data-testid="social-sync-link">
                    <Share2 className="w-4 h-4 mr-2" />
                    Open Social Sync Settings
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </a>
                </Button>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Coin Purchase Modal */}
      <CoinPurchaseModal 
        open={showCoinModal} 
        onOpenChange={setShowCoinModal}
        getAuthHeaders={getAuthHeaders}
      />
      
      {/* Chart Request Modal */}
      <ChartRequestModal
        isOpen={showChartRequestModal}
        onClose={() => {
          setShowChartRequestModal(false);
          setChartRequestTrack(null);
        }}
        track={chartRequestTrack}
      />
      
      {/* Edit Track Modal */}
      <EditTrackModal
        track={editingTrack}
        isOpen={showEditTrackModal}
        onClose={() => {
          setShowEditTrackModal(false);
          setEditingTrack(null);
        }}
        onUpdate={(updatedTrack) => {
          // Update the track in the local state
          setTracks(prev => prev.map(t => 
            t.track_id === updatedTrack.track_id ? { ...t, ...updatedTrack } : t
          ));
        }}
      />
    </div>
  );
}
